import formatter from '../../common/helpers/formatter';
import groupForm from '../../documentation/fhbfForm/counselingCenterForm/forms/groupForm';
import socialMediaForm from '../../documentation/fbForm/forms/socialMediaForm';
import type { Documentation, DocumentationType } from '../../common/types/documentation';
import type {
  BaseDocumentationStatistic,
  SocialMediaStatistic,
  DocumentationStatistic,
} from '../../common/types/statistics/fbStatistics';
import filter from '../../common/helpers/filter';
import type { StatisticsFilter } from '../../common/types/statistics/statistics';
import documentationHelpers from '../../documentation/helpers/documentationHelpers';
import { Setting } from '../../common/types/settings';

const getBaseStatistics = (
  baseDocumentation: Documentation[],
  statisticsFilter: StatisticsFilter,
): BaseDocumentationStatistic => {
  const filteredBaseDocumentation = baseDocumentation.filter((doc) => filter.isInYear(doc.date, statisticsFilter.year));

  const { hours, participants } = filteredBaseDocumentation.reduce(
    (statistics: { hours: number; participants: number }, documentation: Documentation) => ({
      hours: statistics.hours + (documentation.hours || 0),
      participants: statistics.participants + (documentation.participants || 0),
    }),
    { hours: 0, participants: 0 },
  );

  return {
    numberOfAppointments: filteredBaseDocumentation.length,
    numberOfHours: hours,
    numberOfParticipants: participants,
  };
};

const getGroupStatistics = (
  documentations: Documentation[],
  statisticsFilter: StatisticsFilter,
  groups: Setting[],
): Record<string, BaseDocumentationStatistic> => {
  const groupOptions = formatter.getFieldSelectOptions('group', groupForm(groups).fields);

  return groupOptions.reduce((statistics, option) => {
    const groupByType = documentations.filter((documentation) => documentation.group === option.value);

    return {
      ...statistics,
      [option.value]: getBaseStatistics(groupByType, statisticsFilter),
    };
  }, {});
};

const getSocialMediaStatistics = (
  socialMedia: Documentation[],
  statisticsFilter: StatisticsFilter,
): SocialMediaStatistic => {
  const filteredSocialMedia = socialMedia.filter((media) => filter.isInYear(media.date, statisticsFilter.year));

  const socialMediaOptions = formatter.getFieldSelectOptions('socialMedia', socialMediaForm.fields);

  return socialMediaOptions.reduce((prevStatistics, option) => {
    const socialMediaByPlatform = filteredSocialMedia.filter((socialMedia) => socialMedia.socialMedia === option.value);

    const statistics = socialMediaByPlatform.reduce(
      (prev, entry) => ({
        numberOfFollowers: prev.numberOfFollowers + (entry.follower || 0),
        numberOfPosts: prev.numberOfFollowers + (entry.posts || 0),
      }),
      {
        numberOfFollowers: 0,
        numberOfPosts: 0,
      },
    );

    return { ...prevStatistics, [option.value]: statistics };
  }, {});
};

type StatisticConfig = {
  key: DocumentationType;
  getStatistic?: (
    documentations: Documentation[],
    statisticsFilter: StatisticsFilter,
    groups: Setting[],
  ) => BaseDocumentationStatistic | Record<string, BaseDocumentationStatistic> | SocialMediaStatistic;
};

const statisticConfig: StatisticConfig[] = [
  {
    key: 'workgroup',
  },
  {
    key: 'group',
    getStatistic: (documentations, statisticsFilter, groups) =>
      getGroupStatistics(documentations, statisticsFilter, groups),
  },
  {
    key: 'supervision',
  },
  {
    key: 'takeFurtherEducation',
  },
  {
    key: 'giveFurtherEducation',
  },
  {
    key: 'meetingIntern',
  },
  {
    key: 'meetingExtern',
  },
  {
    key: 'managementTask',
  },
  {
    key: 'publicRelation',
  },
  {
    key: 'socialMedia',
    getStatistic: (documentations, statisticsFilter) => getSocialMediaStatistics(documentations, statisticsFilter),
  },
];

const createDocumentationStatistics = (
  documentation: Documentation[],
  statisticsFilter: StatisticsFilter,
  groups: Setting[],
): DocumentationStatistic => {
  return statisticConfig.reduce<DocumentationStatistic>((prev, cur) => {
    const filteredDocumentation = documentationHelpers.filterDocumentationsByType(documentation, cur.key);
    const statistic =
      cur.getStatistic != null
        ? cur.getStatistic(filteredDocumentation, statisticsFilter, groups)
        : getBaseStatistics(filteredDocumentation, statisticsFilter);

    return {
      ...prev,
      [cur.key]: statistic,
    };
  }, {} as DocumentationStatistic);
};

export default createDocumentationStatistics;
